import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import {
  Users,
  BookOpen,
  MessageSquare,
  TrendingUp,
  GraduationCap,
  Calendar,
  ArrowRight,
  FileText,
  Award,
  Trophy,
  Heart,
  Flame,
  Compass,
  Megaphone,
  Ear,
  Link,
  Footprints,
  HeartHandshake,
  BarChart3,
  AlertTriangle,
  Sparkles,
  Pin,
  X,
  Image as ImageIcon,
  Plus,
  Trash2,
  Pencil,
  GripVertical,
  ImageDown,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SuccessStoriesCarousel, GALLERY_CATEGORIES } from '../components/SuccessStoriesCarousel';
import { FeedbackAdminPanel } from '../components/FeedbackAdminPanel';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DownloadInsightsCard = ({ userRole }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/download-insights`, { params: { user_role: userRole } })
      .then((r) => setData(r.data)).catch(() => {});
  }, [userRole]);
  if (!data) return null;
  return (
    <Card className="dashboard-card !h-auto" data-testid="download-insights-card">
      <CardHeader className="flex flex-row items-center justify-between pb-4">
        <CardTitle className="font-['Manrope'] text-xl font-bold text-[#1A1A1A] flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-[#b34e00]" />
          Download Insights
        </CardTitle>
        <Badge className="bg-[#34657f] text-white" data-testid="total-downloads-badge">{data.total_downloads} total downloads</Badge>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <p className="text-sm font-semibold text-[#5C5C5C] uppercase tracking-wide mb-3">Most downloaded sections</p>
            <div className="space-y-2">
              {data.top_folders.slice(0, 6).map((f) => (
                <div key={f.id} className="flex items-center gap-2" data-testid={`insight-folder-${f.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[#1A1A1A] truncate">{f.name}</p>
                    <div className="h-1.5 rounded-full bg-slate-100 mt-1">
                      <div className="h-1.5 rounded-full bg-[#34657f]" style={{ width: `${Math.max(6, Math.round((f.downloads / (data.top_folders[0]?.downloads || 1)) * 100))}%` }} />
                    </div>
                  </div>
                  <span className="text-sm font-semibold text-[#34657f] shrink-0">{f.downloads}</span>
                </div>
              ))}
              {data.top_folders.length === 0 && <p className="text-sm text-[#5C5C5C]">No downloads recorded yet.</p>}
            </div>
          </div>
          <div>
            <p className="text-sm font-semibold text-[#5C5C5C] uppercase tracking-wide mb-3">Most downloaded files</p>
            <div className="space-y-2">
              {data.top_documents.slice(0, 6).map((d) => (
                <div key={d.id} className="flex items-center gap-2" data-testid={`insight-doc-${d.id}`}>
                  <FileText className="w-4 h-4 text-[#3d7a22] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[#1A1A1A] truncate">{d.title}</p>
                    {d.folder_name && <p className="text-xs text-[#5C5C5C] truncate">{d.folder_name}</p>}
                  </div>
                  <span className="text-sm font-semibold text-[#34657f] shrink-0">{d.download_count}</span>
                </div>
              ))}
              {data.top_documents.length === 0 && <p className="text-sm text-[#5C5C5C]">No downloads recorded yet.</p>}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const DashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentPosts, setRecentPosts] = useState([]);
  const [students, setStudents] = useState([]);
  const [forumAnalytics, setForumAnalytics] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [pinnedPosts, setPinnedPosts] = useState([]);
  const [dismissedPinned, setDismissedPinned] = useState(() => {
    try { return JSON.parse(localStorage.getItem('dismissed-pinned') || '[]'); } catch (e) { return []; }
  });
  const isAdmin = user?.role === 'system_admin' || user?.role === 'team_admin';
  const isSystemAdmin = user?.role === 'system_admin';

  const dismissPinned = (post) => {
    const key = `${post.id}:${post.pinned_at}`;
    const next = [...dismissedPinned, key];
    setDismissedPinned(next);
    localStorage.setItem('dismissed-pinned', JSON.stringify(next));
  };
  const visiblePinned = pinnedPosts.filter(p => !dismissedPinned.includes(`${p.id}:${p.pinned_at}`));

  const [homePhotos, setHomePhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [pendingPhotos, setPendingPhotos] = useState([]); // [{ file, caption, category }]
  const [galleryFilter, setGalleryFilter] = useState('all');
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const isDemo = user?.role === 'demo';

  const visiblePhotos = homePhotos.filter(
    (p) => galleryFilter === 'all' || (p.category || 'Great Stories & Photos!') === galleryFilter
  );

  useEffect(() => {
    if (lightboxIndex === null) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setLightboxIndex(null);
      if (e.key === 'ArrowRight') setLightboxIndex((i) => (i + 1) % visiblePhotos.length);
      if (e.key === 'ArrowLeft') setLightboxIndex((i) => (i - 1 + visiblePhotos.length) % visiblePhotos.length);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxIndex, visiblePhotos.length]);
  const photoInputRef = useRef(null);
  const dragIndex = useRef(null);
  const [welcomeTiles, setWelcomeTiles] = useState([]);
  const tileImageRefs = useRef({});
  const photoCmsRef = useRef(null);
  const [searchParams] = useSearchParams();
  const mediaSrc = (url) => (url && url.startsWith('http')) ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;

  useEffect(() => {
    if (!isLoading && isAdmin && searchParams.get('manage') === 'photos' && photoCmsRef.current) {
      photoCmsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      photoCmsRef.current.classList.add('ring-2', 'ring-[#e87722]');
      const t = setTimeout(() => photoCmsRef.current?.classList.remove('ring-2', 'ring-[#e87722]'), 2200);
      return () => clearTimeout(t);
    }
  }, [isLoading, isAdmin, searchParams]);

  const handleAddPhoto = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setPendingPhotos(files.map((file) => ({ file, caption: '', category: 'Great Stories & Photos!' })));
  };

  const submitPhotos = async () => {
    if (pendingPhotos.length === 0) return;
    setUploadingPhoto(true);
    const uploaded = [];
    let failed = 0;
    for (const item of pendingPhotos) {
      try {
        const fd = new FormData();
        fd.append('file', item.file);
        fd.append('caption', item.caption || '');
        fd.append('category', item.category || 'Great Stories & Photos!');
        fd.append('user_role', user?.role || 'instructor');
        fd.append('created_by_name', user?.name || '');
        const res = await axios.post(`${API}/home-photos/upload`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        uploaded.push(res.data);
      } catch (err) {
        failed += 1;
      }
    }
    if (uploaded.length) setHomePhotos((prev) => [...prev, ...uploaded]);
    if (failed === 0) {
      toast.success(uploaded.length === 1 ? 'Photo added to the gallery' : `${uploaded.length} photos added to the gallery`);
    } else if (uploaded.length) {
      toast.warning(`${uploaded.length} added, ${failed} could not be uploaded`);
    } else {
      toast.error('Failed to add photos');
    }
    setUploadingPhoto(false);
    setPendingPhotos([]);
    if (photoInputRef.current) photoInputRef.current.value = '';
  };

  const downloadGalleryPhoto = async (photo) => {
    try {
      const res = await fetch(mediaSrc(photo.image_url));
      const blob = await res.blob();
      const ext = (blob.type.split('/')[1] || 'jpg').replace('jpeg', 'jpg');
      const base = (photo.caption || 'gallery-photo').replace(/[^A-Za-z0-9 _-]/g, '').replace(/\s+/g, '-').slice(0, 40) || 'gallery-photo';
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${base}.${ext}`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success('Photo downloaded');
    } catch {
      toast.error('Could not download the photo');
    }
  };

  const handleDeletePhoto = async (id) => {
    try {
      await axios.delete(`${API}/home-photos/${id}`, { params: { user_role: user?.role || 'instructor' } });
      setHomePhotos(prev => prev.filter(p => p.id !== id));
      toast.success('Photo removed');
    } catch (err) {
      toast.error('Failed to remove photo');
    }
  };

  const handlePhotoDrop = async (dropIndex) => {
    const from = dragIndex.current;
    dragIndex.current = null;
    if (from === null || from === dropIndex) return;
    const next = [...homePhotos];
    const [moved] = next.splice(from, 1);
    next.splice(dropIndex, 0, moved);
    setHomePhotos(next);
    try {
      await axios.put(`${API}/home-photos/reorder`, { ids: next.map(p => p.id) }, { params: { user_role: user?.role || 'instructor' } });
    } catch (err) {
      toast.error('Failed to save new order');
    }
  };

  const handleEditCaption = async (photo) => {
    const caption = window.prompt('Edit caption:', photo.caption || '');
    if (caption === null) return;
    try {
      await axios.put(`${API}/home-photos/${photo.id}`, { caption }, { params: { user_role: user?.role || 'instructor' } });
      setHomePhotos(prev => prev.map(p => p.id === photo.id ? { ...p, caption } : p));
    } catch (err) {
      toast.error('Failed to update caption');
    }
  };

  const handleTileField = (id, field, value) =>
    setWelcomeTiles(prev => prev.map(t => t.id === id ? { ...t, [field]: value } : t));

  const handleSaveTile = async (tile) => {
    try {
      await axios.put(`${API}/welcome-tiles/${tile.id}`, { title: tile.title, subtitle: tile.subtitle, focal_point: tile.focal_point || '50% 50%' }, { params: { user_role: user?.role || 'instructor' } });
      toast.success('Welcome tile saved');
    } catch (err) {
      toast.error('Failed to save tile');
    }
  };

  const FOCAL_POSITIONS = [
    ['left top', 'center top', 'right top'],
    ['left center', 'center center', 'right center'],
    ['left bottom', 'center bottom', 'right bottom'],
  ];
  const normalizeFocal = (fp) => {
    if (!fp || fp === '50% 50%' || fp === 'center') return 'center center';
    return fp;
  };
  const handleTileFocal = async (tile, pos) => {
    setWelcomeTiles(prev => prev.map(t => t.id === tile.id ? { ...t, focal_point: pos } : t));
    try {
      await axios.put(`${API}/welcome-tiles/${tile.id}`, { focal_point: pos }, { params: { user_role: user?.role || 'instructor' } });
      toast.success('Focus updated');
    } catch (err) {
      toast.error('Failed to update focus');
    }
  };

  const handleTileImage = async (tile, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('user_role', user?.role || 'instructor');
      const res = await axios.post(`${API}/welcome-tiles/${tile.id}/image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setWelcomeTiles(prev => prev.map(t => t.id === tile.id ? res.data : t));
      toast.success('Image updated');
    } catch (err) {
      toast.error('Failed to upload image');
    } finally {
      if (tileImageRefs.current[tile.id]) tileImageRefs.current[tile.id].value = '';
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const requests = [
          axios.get(`${API}/dashboard/stats`),
          axios.get(`${API}/posts`),
        ];
        if (isSystemAdmin) {
          requests.push(axios.get(`${API}/students`));
        }
        if (isAdmin) {
          requests.push(axios.get(`${API}/forum/analytics`));
        }
        const results = await Promise.all(requests);

        setStats(results[0].data);
        setRecentPosts(results[1].data.slice(0, 3));
        let idx = 2;
        if (isSystemAdmin) {
          setStudents(results[idx].data);
          idx++;
        }
        if (isAdmin && results[idx]) {
          setForumAnalytics(results[idx].data);
        }
        try {
          const pinnedRes = await axios.get(`${API}/posts/pinned`);
          setPinnedPosts(pinnedRes.data);
        } catch (e) { /* non-critical */ }
        try {
          const photosRes = await axios.get(`${API}/home-photos`);
          setHomePhotos(photosRes.data);
        } catch (e) { /* non-critical */ }
        try {
          const tilesRes = await axios.get(`${API}/welcome-tiles`);
          setWelcomeTiles(tilesRes.data);
        } catch (e) { /* non-critical */ }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [isAdmin]);

  const getInitials = (name) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const StatCard = ({ icon: Icon, title, value, color, subtitle, to }) => (
    <Card
      className="dashboard-card interactive-card group cursor-pointer"
      data-testid={`stat-${title.toLowerCase().replace(/\s/g, '-')}`}
      role="link"
      tabIndex={0}
      aria-label={`${title}: ${value} — open`}
      onClick={() => to && navigate(to)}
      onKeyDown={(e) => { if (to && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); navigate(to); } }}
    >
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-[#5C5C5C] mb-1">{title}</p>
            <p className="text-3xl font-bold font-['Manrope'] text-[#1A1A1A]">{value}</p>
            {subtitle && (
              <p className="text-xs text-[#5C5C5C] mt-1">{subtitle}</p>
            )}
          </div>
          <div className={`w-12 h-12 rounded-xl ${color} flex items-center justify-center group-hover:scale-110 transition-transform`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  // Badge icon mapping
  const badgeIconMap = {
    'first_step': Footprints,
    'voice_found': Megaphone,
    'community_champion': Trophy,
    'helping_hand': HeartHandshake,
    'active_listener': Ear,
    'thread_explorer': Compass,
    'connector': Link,
    'conversation_spark': Flame,
  };

  const getBadgeDef = (badgeId) => {
    if (!forumAnalytics) return null;
    return forumAnalytics.badge_definitions?.find(b => b.id === badgeId);
  };

  const ThreadStatusBadge = ({ status }) => {
    const config = {
      active: { label: 'Active', className: 'bg-[#6cc24a]/15 text-[#4a8a34] border-[#6cc24a]/30' },
      warming: { label: 'Warming Up', className: 'bg-[#e87722]/15 text-[#a84d09] border-[#e87722]/30' },
      cold: { label: 'Needs Attention', className: 'bg-red-50 text-red-700 border-red-200' },
    };
    const c = config[status] || config.cold;
    return <Badge variant="outline" className={c.className} data-testid={`thread-status-${status}`}>{c.label}</Badge>;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="dashboard-loading">
        <div className="w-8 h-8 border-4 border-[#34657f] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Pinned Announcements Banners */}
      {visiblePinned.length > 0 && (
        <div className="space-y-3" data-testid="dashboard-pinned-banners">
          {visiblePinned.map((post) => (
            <div
              key={post.id}
              className="flex items-start gap-3 rounded-xl border-2 border-[#e87722]/40 bg-[#e87722]/10 px-5 py-4"
              data-testid={`dashboard-pinned-${post.id}`}
            >
              <div className="w-10 h-10 rounded-lg bg-[#b34e00] flex items-center justify-center shrink-0">
                <Pin className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate('/forum')}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-[#1A1A1A]">{post.title}</span>
                  <Badge className="bg-[#b34e00] text-white text-xs">Pinned</Badge>
                </div>
                <p className="text-sm text-[#5C5C5C] line-clamp-2 mt-0.5">{post.content}</p>
                <p className="text-xs text-[#5C5C5C] mt-1">Posted by {post.author_name}</p>
              </div>
              <button
                onClick={() => dismissPinned(post)}
                className="shrink-0 text-[#5C5C5C] hover:text-[#1A1A1A] p-1"
                title="Dismiss"
                data-testid={`dismiss-pinned-${post.id}`}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] text-[#1A1A1A]">
            Dashboard
          </h1>
          <p className="text-[#5C5C5C] mt-1">
            {user?.role === 'system_admin' && 'System overview and management'}
            {user?.role === 'team_admin' && 'Program overview and progress'}
            {user?.role === 'instructor' && 'Your resources, community, and classroom tools'}
            {user?.role === 'skills_trainer' && 'Your resources, community, and classroom tools'}
          </p>
        </div>
        <div className="flex gap-3">
          <Button
            onClick={() => navigate('/curriculum?section=curriculum')}
            className="bg-[#34657f] hover:bg-[#2a5060]"
            data-testid="browse-curriculum-btn"
          >
            <BookOpen className="w-4 h-4 mr-2" />
            Browse Curriculum
          </Button>
        </div>
      </div>

      {/* Stats Grid — system admins only, cards link to their pages */}
      {user?.role === 'system_admin' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            icon={GraduationCap}
            title="Total Students"
            value={stats?.total_students || 0}
            color="bg-[#34657f]"
            subtitle="Enrolled in program"
            to="/students"
          />
          <StatCard
            icon={BookOpen}
            title="Documents"
            value={stats?.total_documents || 0}
            color="bg-[#3d7a22]"
            subtitle="In curriculum library — click to browse"
            to="/curriculum?section=curriculum"
          />
          <StatCard
            icon={MessageSquare}
            title="Forum Posts"
            value={stats?.total_posts || 0}
            color="bg-[#b34e00]"
            subtitle="Community discussions — click to open"
            to="/forum"
          />
          <StatCard
            icon={Award}
            title="Assessments"
            value={stats?.recent_assessments || 0}
            color="bg-[#b34e00]"
            subtitle="Completed this period"
            to="/students"
          />
        </div>
      )}

      {/* Success Stories Carousel */}
      <SuccessStoriesCarousel />

      {/* Homepage Photo Gallery (editable in CMS by admins) */}
      {(homePhotos.length > 0 || !isDemo) && (
        <Card ref={photoCmsRef} className="dashboard-card transition-shadow duration-500 rounded-xl" data-testid="home-photo-gallery">
          <CardHeader className="flex flex-row items-center justify-between pb-4">
            <CardTitle className="font-['Manrope'] text-xl font-bold text-[#1A1A1A] flex items-center gap-2">
              <ImageIcon className="w-5 h-5 text-[#1f7fa6]" />
              Photo Gallery
            </CardTitle>
            {!isDemo && (
              <>
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={handleAddPhoto}
                  data-testid="home-photo-input"
                />
                <Button
                  size="sm"
                  className="bg-[#b34e00] hover:bg-[#a84d09]"
                  disabled={uploadingPhoto}
                  onClick={() => photoInputRef.current?.click()}
                  data-testid="add-home-photo-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  {uploadingPhoto ? 'Uploading…' : 'Add Photo'}
                </Button>
              </>
            )}
          </CardHeader>
          <CardContent>
            {homePhotos.length > 0 && (
              <div className="flex gap-1.5 flex-wrap mb-4" data-testid="gallery-category-tabs">
                {['all', ...GALLERY_CATEGORIES].map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => setGalleryFilter(cat) || setLightboxIndex(null)}
                    className={`h-7 px-3 rounded-full text-xs font-semibold border transition-all ${
                      galleryFilter === cat
                        ? 'bg-[#34657f] border-[#34657f] text-white shadow-sm'
                        : 'bg-white border-slate-300 text-slate-500 hover:border-[#34657f]/50 hover:text-slate-700'
                    }`}
                    data-testid={`gallery-tab-${cat === 'all' ? 'all' : cat.replace(/[^a-z]/gi, '-').toLowerCase()}`}
                  >
                    {cat === 'all' ? 'All' : cat}
                  </button>
                ))}
              </div>
            )}
            {isAdmin && homePhotos.length > 1 && (
              <p className="text-xs text-[#5C5C5C] mb-3">Drag photos to reorder · hover to edit caption or remove.</p>
            )}
            {homePhotos.length === 0 ? (
              <p className="text-sm text-[#5C5C5C] text-center py-6">
                No photos yet. Click "Add Photo" to showcase your program.
              </p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {visiblePhotos.map((photo, index) => (
                  <div
                    key={photo.id}
                    className="relative group rounded-xl overflow-hidden bg-slate-100 aspect-square"
                    data-testid={`home-photo-${photo.id}`}
                    draggable={isAdmin}
                    onDragStart={() => { dragIndex.current = index; }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => handlePhotoDrop(index)}
                  >
                    <img
                      src={mediaSrc(photo.image_url)}
                      alt={photo.caption || 'Gallery photo'}
                      className="w-full h-full object-cover cursor-pointer transition-transform duration-300 group-hover:scale-[1.03]"
                      onClick={() => setLightboxIndex(index)}
                      data-testid={`open-lightbox-${photo.id}`}
                    />
                    {(isAdmin || (photo.created_by && photo.created_by === user?.id)) && (
                      <>
                        {isAdmin && (
                          <>
                            <div className="absolute top-1.5 left-1.5 w-7 h-7 rounded-full bg-black/40 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-grab">
                              <GripVertical className="w-3.5 h-3.5" />
                            </div>
                            <button
                              onClick={() => downloadGalleryPhoto(photo)}
                              className="absolute top-1.5 right-[4.5rem] w-7 h-7 rounded-full bg-[#3d7a22] text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                              title="Download photo"
                              aria-label="Download photo"
                              data-testid={`download-gallery-photo-${photo.id}`}
                            >
                              <ImageDown className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => handleEditCaption(photo)}
                              className="absolute top-1.5 right-9 w-7 h-7 rounded-full bg-[#34657f] text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                              title="Edit caption"
                              data-testid={`edit-caption-${photo.id}`}
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => handleDeletePhoto(photo.id)}
                          className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Remove photo"
                          data-testid={`delete-home-photo-${photo.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                    {photo.caption && (
                      <div className="absolute bottom-0 inset-x-0 bg-black/50 text-white text-xs px-2 py-1 truncate">
                        {photo.caption}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        )}

        {/* Full-screen lightbox */}
        {lightboxIndex !== null && visiblePhotos[lightboxIndex] && (
          <div
            className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center"
            onClick={() => setLightboxIndex(null)}
            data-testid="gallery-lightbox"
          >
            <button
              type="button"
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center transition-colors"
              onClick={(e) => { e.stopPropagation(); setLightboxIndex(null); }}
              aria-label="Close"
              data-testid="lightbox-close"
            >
              <X className="w-5 h-5" />
            </button>
            {visiblePhotos.length > 1 && (
              <>
                <button
                  type="button"
                  className="absolute left-3 sm:left-6 w-11 h-11 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center transition-colors"
                  onClick={(e) => { e.stopPropagation(); setLightboxIndex((i) => (i - 1 + visiblePhotos.length) % visiblePhotos.length); }}
                  aria-label="Previous photo"
                  data-testid="lightbox-prev"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  type="button"
                  className="absolute right-3 sm:right-6 w-11 h-11 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center transition-colors"
                  onClick={(e) => { e.stopPropagation(); setLightboxIndex((i) => (i + 1) % visiblePhotos.length); }}
                  aria-label="Next photo"
                  data-testid="lightbox-next"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </>
            )}
            <div className="max-w-5xl max-h-[85vh] px-14 flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
              <img
                src={mediaSrc(visiblePhotos[lightboxIndex].image_url)}
                alt={visiblePhotos[lightboxIndex].caption || 'Gallery photo'}
                className="max-h-[72vh] max-w-full object-contain rounded-lg shadow-2xl"
                data-testid="lightbox-image"
              />
              <div className="mt-4 text-center">
                {visiblePhotos[lightboxIndex].caption && (
                  <p className="text-white text-base font-medium" data-testid="lightbox-caption">{visiblePhotos[lightboxIndex].caption}</p>
                )}
                <p className="text-white/60 text-xs mt-1">
                  {(visiblePhotos[lightboxIndex].category || 'Great Stories & Photos!')} · {lightboxIndex + 1} / {visiblePhotos.length}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Photo upload dialog (per-photo caption + shared category) */}
        <Dialog open={pendingPhotos.length > 0} onOpenChange={(open) => { if (!open && !uploadingPhoto) setPendingPhotos([]); }}>
          <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="photo-upload-dialog">
            <DialogHeader>
              <DialogTitle className="font-['Manrope']">
                {pendingPhotos.length === 1 ? 'Add photo to the gallery' : `Add ${pendingPhotos.length} photos to the gallery`}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <p className="text-xs text-[#5C5C5C]">Give each photo its own category and caption.</p>
              <div className="space-y-4">
                {pendingPhotos.map((item, idx) => (
                  <div key={idx} className="flex gap-3 items-start border border-slate-100 rounded-lg p-2" data-testid={`photo-upload-item-${idx}`}>
                    <img
                      src={URL.createObjectURL(item.file)}
                      alt={`Preview ${idx + 1}`}
                      className="w-20 h-20 object-cover rounded-lg border border-slate-200 shrink-0"
                    />
                    <div className="flex-1 min-w-0 space-y-2">
                      <div>
                        <Label className="text-xs text-[#5C5C5C]">Category *</Label>
                        <Select value={item.category} onValueChange={(v) => setPendingPhotos((prev) => prev.map((p, i) => (i === idx ? { ...p, category: v } : p)))}>
                          <SelectTrigger data-testid={`photo-category-select-${idx}`} className="h-9">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {GALLERY_CATEGORIES.map((c) => (
                              <SelectItem key={c} value={c}>{c}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor={`photo-caption-${idx}`} className="text-xs text-[#5C5C5C]">Caption (optional)</Label>
                        <Input
                          id={`photo-caption-${idx}`}
                          placeholder="e.g., Stocking shelves at the jobsite"
                          value={item.caption}
                          onChange={(e) => {
                            const val = e.target.value;
                            setPendingPhotos((prev) => prev.map((p, i) => (i === idx ? { ...p, caption: val } : p)));
                          }}
                          data-testid={`photo-caption-input-${idx}`}
                        />
                      </div>
                      {pendingPhotos.length > 1 && (
                        <button
                          type="button"
                          className="text-xs text-red-600 hover:underline"
                          onClick={() => setPendingPhotos((prev) => prev.filter((_, i) => i !== idx))}
                          data-testid={`photo-remove-${idx}`}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPendingPhotos([])} disabled={uploadingPhoto}>Cancel</Button>
              <Button onClick={submitPhotos} disabled={uploadingPhoto} className="bg-[#b34e00] hover:bg-[#a84d09]" data-testid="photo-upload-submit">
                {uploadingPhoto ? 'Uploading…' : (pendingPhotos.length === 1 ? 'Add Photo' : `Add ${pendingPhotos.length} Photos`)}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Welcome Screen editor (admin only) */}
        {isAdmin && welcomeTiles.length > 0 && (
          <Card className="dashboard-card" data-testid="welcome-screen-editor">
            <CardHeader className="pb-4">
              <CardTitle className="font-['Manrope'] text-xl font-bold text-[#1A1A1A] flex items-center gap-2">
                <ImageIcon className="w-5 h-5 text-[#b34e00]" />
                Welcome Screen
              </CardTitle>
              <p className="text-sm text-[#5C5C5C]">Edit the photo shown on the login/welcome screen.</p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {welcomeTiles.slice(0, 1).map((tile) => (
                  <div key={tile.id} className="flex gap-3 p-3 rounded-xl border border-slate-200" data-testid={`welcome-tile-${tile.id}`}>
                    <div className="relative w-24 h-24 rounded-lg overflow-hidden bg-slate-100 shrink-0 group">
                      <img src={mediaSrc(tile.image_url)} alt={tile.title} style={{ objectPosition: tile.focal_point || '50% 50%' }} className="w-full h-full object-cover" />
                      <input
                        ref={(el) => { tileImageRefs.current[tile.id] = el; }}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleTileImage(tile, e)}
                      />
                      <button
                        onClick={() => tileImageRefs.current[tile.id]?.click()}
                        className="absolute inset-0 bg-black/40 text-white text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`welcome-tile-image-${tile.id}`}
                      >
                        Change
                      </button>
                    </div>
                    <div className="flex-1 min-w-0 space-y-2">
                      <Input
                        value={tile.title}
                        onChange={(e) => handleTileField(tile.id, 'title', e.target.value)}
                        placeholder="Title (optional)"
                        className="h-9"
                        data-testid={`welcome-tile-title-${tile.id}`}
                      />
                      <Input
                        value={tile.subtitle}
                        onChange={(e) => handleTileField(tile.id, 'subtitle', e.target.value)}
                        placeholder="Subtitle (optional)"
                        className="h-9"
                        data-testid={`welcome-tile-subtitle-${tile.id}`}
                      />
                      <div className="flex items-center gap-3 pt-1">
                        <div>
                          <p className="text-[11px] font-medium text-[#5C5C5C] mb-1">Focus (keeps faces in frame)</p>
                          <div className="grid grid-cols-3 gap-0.5 w-[78px]" data-testid={`welcome-tile-focus-${tile.id}`}>
                            {FOCAL_POSITIONS.flat().map((pos) => {
                              const active = normalizeFocal(tile.focal_point) === pos;
                              return (
                                <button
                                  key={pos}
                                  type="button"
                                  onClick={() => handleTileFocal(tile, pos)}
                                  title={pos}
                                  aria-label={`Focus ${pos}`}
                                  aria-pressed={active}
                                  data-testid={`focus-${pos.replace(' ', '-')}-${tile.id}`}
                                  className={`w-6 h-6 rounded-sm border transition-colors ${active ? 'bg-[#b34e00] border-[#e87722]' : 'bg-slate-100 border-slate-300 hover:bg-slate-200'}`}
                                />
                              );
                            })}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSaveTile(tile)}
                          data-testid={`welcome-tile-save-${tile.id}`}
                        >
                          Save
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

      {/* User Feedback (admins only) */}
      {isAdmin && <FeedbackAdminPanel />}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Student Progress Section (system admins only) */}
        {isSystemAdmin && (
        <div className="lg:col-span-8">
          <Card className="dashboard-card" data-testid="student-progress-card">
            <CardHeader className="flex flex-row items-center justify-between pb-4">
              <CardTitle className="font-['Manrope'] text-xl font-bold text-[#1A1A1A]">
                Student Progress
              </CardTitle>
              <Button
                variant="ghost"
                className="text-[#34657f] hover:text-[#2a5060]"
                onClick={() => navigate('/students')}
                data-testid="view-all-students-btn"
              >
                View All
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {students.slice(0, 3).map((student) => (
                  <div
                    key={student.id}
                    className="flex items-center gap-4 p-4 rounded-xl bg-[#F8F9FA] hover:bg-slate-100 transition-colors"
                    data-testid={`student-row-${student.id}`}
                  >
                    <Avatar className="h-12 w-12">
                      <AvatarImage src={student.avatar_url} alt={student.name} />
                      <AvatarFallback className="bg-[#34657f] text-white font-semibold">
                        {getInitials(student.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-semibold text-[#1A1A1A] truncate">
                          {student.name}
                        </p>
                        <span className="badge-blue">{student.student_id}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Progress value={Math.floor(Math.random() * 30) + 60} aria-label={`${student.name} progress`} className="flex-1 h-2" />
                        <span className="text-sm font-medium text-[#3d7a22]">
                          {Math.floor(Math.random() * 30) + 60}%
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
                
                {students.length === 0 && (
                  <div className="text-center py-8 text-[#5C5C5C]">
                    <GraduationCap className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No students enrolled yet</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
        )}

        {/* Quick Actions & Recent Activity */}
        <div className={isSystemAdmin ? 'lg:col-span-4 space-y-6' : 'lg:col-span-12 grid grid-cols-1 lg:grid-cols-2 gap-6 items-start'}>
          {/* Quick Actions */}
          <Card className="dashboard-card !h-auto" data-testid="quick-actions-card">
            <CardHeader className="pb-4">
              <CardTitle className="font-['Manrope'] text-xl font-bold text-[#1A1A1A]">
                Quick Actions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button
                variant="outline"
                className="w-full justify-start h-12 border-slate-200 hover:border-[#34657f] hover:bg-[#34657f]/5"
                onClick={() => navigate('/curriculum?section=curriculum')}
                data-testid="quick-browse-docs-btn"
              >
                <FileText className="w-5 h-5 mr-3 text-[#34657f]" />
                Browse Documents
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start h-12 border-slate-200 hover:border-[#e87722] hover:bg-[#e87722]/5"
                onClick={() => navigate('/forum')}
                data-testid="quick-view-forum-btn"
              >
                <MessageSquare className="w-5 h-5 mr-3 text-[#b34e00]" />
                View Forum
              </Button>
              {isAdmin && (
                <Button
                  variant="outline"
                  className="w-full justify-start h-12 border-slate-200 hover:border-[#6cc24a] hover:bg-[#6cc24a]/5"
                  onClick={() => navigate('/students')}
                  data-testid="quick-student-progress-btn"
                >
                  <TrendingUp className="w-5 h-5 mr-3 text-[#3d7a22]" />
                  Student Progress
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Upcoming Events */}
          <Card className="dashboard-card !h-auto" data-testid="upcoming-events-card">
            <CardHeader className="pb-4">
              <CardTitle className="font-['Manrope'] text-xl font-bold text-[#1A1A1A]">
                Upcoming
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-3 p-3 rounded-lg bg-[#F8F9FA]">
                <div className="w-10 h-10 rounded-lg bg-[#34657f] flex items-center justify-center shrink-0">
                  <Calendar className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-[#1A1A1A] text-sm">
                    Assessment Due
                  </p>
                  <p className="text-xs text-[#5C5C5C]">
                    Work Skills - Dec 20
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 rounded-lg bg-[#F8F9FA]">
                <div className="w-10 h-10 rounded-lg bg-[#3d7a22] flex items-center justify-center shrink-0">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-[#1A1A1A] text-sm">
                    Team Meeting
                  </p>
                  <p className="text-xs text-[#5C5C5C]">
                    Weekly sync - Dec 18
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {isAdmin && <DownloadInsightsCard userRole={user?.role} />}

      {/* Recent Forum Activity */}
      <Card className="dashboard-card" data-testid="recent-forum-card">
        <CardHeader className="flex flex-row items-center justify-between pb-4">
          <CardTitle className="font-['Manrope'] text-xl font-bold text-[#1A1A1A]">
            Recent Forum Activity
          </CardTitle>
          <Button
            variant="ghost"
            className="text-[#34657f] hover:text-[#2a5060]"
            onClick={() => navigate('/forum')}
            data-testid="view-all-posts-btn"
          >
            View All
            <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {recentPosts.map((post) => (
              <div
                key={post.id}
                className="p-4 rounded-xl border border-slate-200 hover:border-[#34657f]/30 hover:shadow-md transition-all cursor-pointer"
                onClick={() => navigate('/forum')}
                data-testid={`post-card-${post.id}`}
              >
                <div className="flex items-center gap-2 mb-3">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={post.author_avatar} alt={post.author_name} />
                    <AvatarFallback className="bg-[#b34e00] text-white text-xs">
                      {getInitials(post.author_name)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-sm font-medium text-[#1A1A1A] truncate">
                    {post.author_name}
                  </span>
                </div>
                <h3 className="font-semibold text-[#1A1A1A] mb-2 line-clamp-2">
                  {post.title}
                </h3>
                <p className="text-sm text-[#5C5C5C] line-clamp-2">
                  {post.content}
                </p>
                <div className="flex items-center gap-4 mt-3 text-xs text-[#5C5C5C]">
                  <span>{post.likes?.length || 0} likes</span>
                  <span>{post.comments?.length || 0} comments</span>
                </div>
              </div>
            ))}
            
            {recentPosts.length === 0 && (
              <div className="col-span-3 text-center py-8 text-[#5C5C5C]">
                <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No forum posts yet</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Forum Analytics - Admin Only */}
      {isAdmin && forumAnalytics && (
        <>
          <div className="flex items-center gap-3 pt-4">
            <BarChart3 className="w-6 h-6 text-[#34657f]" />
            <h2 className="text-2xl font-bold font-['Manrope'] text-[#1A1A1A]">Forum Analytics</h2>
            <Badge variant="outline" className="bg-[#34657f]/10 text-[#34657f] border-[#34657f]/30">Admin</Badge>
          </div>

          {/* Forum Overview Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card className="dashboard-card" data-testid="analytics-total-posts">
              <CardContent className="p-5 flex items-center gap-4">
                <div className="w-11 h-11 rounded-xl bg-[#b34e00] flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold font-['Manrope']">{forumAnalytics.total_posts}</p>
                  <p className="text-xs text-[#5C5C5C]">Total Posts</p>
                </div>
              </CardContent>
            </Card>
            <Card className="dashboard-card" data-testid="analytics-total-comments">
              <CardContent className="p-5 flex items-center gap-4">
                <div className="w-11 h-11 rounded-xl bg-[#1f7fa6] flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold font-['Manrope']">{forumAnalytics.total_comments}</p>
                  <p className="text-xs text-[#5C5C5C]">Total Comments</p>
                </div>
              </CardContent>
            </Card>
            <Card className="dashboard-card" data-testid="analytics-total-likes">
              <CardContent className="p-5 flex items-center gap-4">
                <div className="w-11 h-11 rounded-xl bg-[#3d7a22] flex items-center justify-center">
                  <Heart className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold font-['Manrope']">{forumAnalytics.total_likes}</p>
                  <p className="text-xs text-[#5C5C5C]">Total Likes</p>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Top Users Leaderboard */}
            <div className="lg:col-span-8">
              <Card className="dashboard-card" data-testid="top-users-card">
                <CardHeader className="flex flex-row items-center justify-between pb-4">
                  <CardTitle className="font-['Manrope'] text-xl font-bold text-[#1A1A1A] flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-[#b34e00]" />
                    Top Contributors
                  </CardTitle>
                  <p className="text-sm text-[#5C5C5C]">For swag & awards</p>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {forumAnalytics.top_users.slice(0, 8).map((u, idx) => (
                      <div
                        key={u.author_id}
                        className={`flex items-center gap-4 p-3 rounded-xl transition-colors ${
                          idx === 0 ? 'bg-[#e87722]/5 border border-[#e87722]/20' :
                          idx === 1 ? 'bg-[#34657f]/5 border border-[#34657f]/15' :
                          idx === 2 ? 'bg-[#6cc24a]/5 border border-[#6cc24a]/15' :
                          'bg-[#F8F9FA] hover:bg-slate-100'
                        }`}
                        data-testid={`leaderboard-user-${u.author_id}`}
                      >
                        {/* Rank */}
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${
                          idx === 0 ? 'bg-[#b34e00] text-white' :
                          idx === 1 ? 'bg-[#34657f] text-white' :
                          idx === 2 ? 'bg-[#3d7a22] text-white' :
                          'bg-slate-200 text-[#5C5C5C]'
                        }`}>
                          {idx + 1}
                        </div>

                        {/* Avatar */}
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={u.author_avatar} alt={u.author_name} />
                          <AvatarFallback className="bg-[#34657f] text-white text-sm font-semibold">
                            {getInitials(u.author_name)}
                          </AvatarFallback>
                        </Avatar>

                        {/* Name + Badges */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-semibold text-[#1A1A1A] truncate">{u.author_name}</p>
                            <TooltipProvider>
                              <div className="flex gap-1">
                                {u.badges.map(bid => {
                                  const bdef = getBadgeDef(bid);
                                  if (!bdef) return null;
                                  const IconComp = badgeIconMap[bid] || Award;
                                  return (
                                    <Tooltip key={bid}>
                                      <TooltipTrigger aria-label={bdef.name || 'Badge'}>
                                        <span
                                          className="w-6 h-6 rounded-full flex items-center justify-center"
                                          style={{ backgroundColor: bdef.color + '20' }}
                                          data-testid={`badge-${bid}`}
                                        >
                                          <IconComp className="w-3.5 h-3.5" style={{ color: bdef.color }} />
                                        </span>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p className="font-semibold">{bdef.name}</p>
                                        <p className="text-xs">{bdef.description}</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  );
                                })}
                              </div>
                            </TooltipProvider>
                          </div>
                          <div className="flex gap-4 text-xs text-[#5C5C5C] mt-0.5">
                            <span>{u.post_count} posts</span>
                            <span>{u.comment_count} comments</span>
                            <span>{u.likes_received} likes received</span>
                          </div>
                        </div>

                        {/* Engagement Score */}
                        <div className="text-right shrink-0">
                          <p className="text-lg font-bold text-[#1A1A1A]">{u.engagement_score}</p>
                          <p className="text-xs text-[#5C5C5C]">score</p>
                        </div>
                      </div>
                    ))}
                    {forumAnalytics.top_users.length === 0 && (
                      <div className="text-center py-8 text-[#5C5C5C]">
                        <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>No forum activity yet</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Thread Engagement + Badge Legend */}
            <div className="lg:col-span-4 space-y-6">
              {/* Thread Engagement */}
              <Card className="dashboard-card" data-testid="thread-engagement-card">
                <CardHeader className="pb-3">
                  <CardTitle className="font-['Manrope'] text-lg font-bold text-[#1A1A1A] flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-[#34657f]" />
                    Thread Engagement
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {forumAnalytics.thread_stats.map(t => {
                    const maxPosts = Math.max(...forumAnalytics.thread_stats.map(ts => ts.post_count), 1);
                    return (
                      <div key={t.thread_id} className="p-3 rounded-lg bg-[#F8F9FA]" data-testid={`thread-stat-${t.thread_id}`}>
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-sm font-semibold text-[#1A1A1A] truncate flex-1">{t.thread_name}</p>
                          <ThreadStatusBadge status={t.status} />
                        </div>
                        <div className="h-2 rounded-full bg-slate-200 overflow-hidden mb-2">
                          <div
                            className="h-full rounded-full transition-all"
                            style={{ width: `${(t.post_count / maxPosts) * 100}%`, backgroundColor: t.thread_color }}
                          />
                        </div>
                        <div className="flex gap-3 text-xs text-[#5C5C5C]">
                          <span>{t.post_count} posts</span>
                          <span>{t.unique_contributors} contributors</span>
                        </div>
                        {t.status === 'cold' && (
                          <div className="flex items-center gap-1 mt-2 text-xs text-red-700">
                            <AlertTriangle className="w-3 h-3" />
                            <span>No posts yet — consider promoting this thread</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </CardContent>
              </Card>

              {/* Badge Legend */}
              <Card className="dashboard-card" data-testid="badge-legend-card">
                <CardHeader className="pb-3">
                  <CardTitle className="font-['Manrope'] text-lg font-bold text-[#1A1A1A] flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-[#b34e00]" />
                    Badges & Rewards
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {forumAnalytics.badge_definitions.map(b => {
                    const IconComp = badgeIconMap[b.id] || Award;
                    const earnedCount = forumAnalytics.top_users.filter(u => u.badges.includes(b.id)).length;
                    return (
                      <div key={b.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-[#F8F9FA] transition-colors" data-testid={`badge-def-${b.id}`}>
                        <span
                          className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
                          style={{ backgroundColor: b.color + '20' }}
                        >
                          <IconComp className="w-4 h-4" style={{ color: b.color }} />
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-[#1A1A1A]">{b.name}</p>
                          <p className="text-xs text-[#5C5C5C] truncate">{b.description}</p>
                        </div>
                        <Badge variant="outline" className="text-xs shrink-0">
                          {earnedCount} earned
                        </Badge>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DashboardPage;
